/**
 * Which authored steps a launch actually renders — the two rules, and nothing else.
 *
 * A sibling module like `walkthroughAnchors` and `walkthroughRoute`: these are pure list transforms over
 * the resolved steps, so they belong beside the hook rather than inside it, and they can be exercised
 * without pulling in navigation, analytics and the DOM scan the hook needs.
 */
import type { Step } from "react-joyride";
import type { AnnieWalkthroughStep } from "../../../utils/annieStream";
import { anchorSelector, scrollAnchorIntoView } from "./walkthroughAnchors";

/**
 * The steps the anchor scan can prove: on screen the moment the tour starts.
 *
 * The rest are deferred — they mount only after the user acts on a control an earlier step points at, so
 * they are known-absent at scan time by construction and the scan must not look for them.
 */
export const eagerSteps = (steps: AnnieWalkthroughStep[]): AnnieWalkthroughStep[] => steps.filter((step) => step.reveal === null);

const byOrder = (left: AnnieWalkthroughStep, right: AnnieWalkthroughStep): number => left.order - right.order;

/**
 * The steps that will actually render, in authored order.
 *
 * An eager step survives when the scan SAW it. A deferred step survives only when the nearest eager step
 * BEFORE it did — that step's control is what reveals this one, so an opener the department does not have
 * means nothing can ever reveal it. Without this rule a dropped opener strands its deferred step in the
 * tour: nobody is told to open anything, joyride sits out `targetWaitTimeout` on a control that cannot
 * arrive, and the progress counter promised a step that never renders. Medical (Synergy) departments have
 * no Staffing settings panel, which is exactly that shape.
 *
 * The opener is POSITIONAL rather than named: `_schema.md` requires a deferred step to be authored
 * immediately after the step that opens it, so the nearest preceding eager step already is the opener and
 * an explicit `revealed_by` would only restate the order field.
 */
export const survivingSteps = (
	steps: AnnieWalkthroughStep[],
	visibleAnchors: Set<string>,
	blockedOpeners: ReadonlySet<string> = new Set(),
): AnnieWalkthroughStep[] => {
	const kept: AnnieWalkthroughStep[] = [];
	let openerVisible = false;
	let openerCanReveal = true;
	for (const step of [...steps].sort(byOrder)) {
		if (step.reveal === null) {
			openerVisible = visibleAnchors.has(step.anchor);
			// An opener can be PRESENT and still unable to open anything — every reveal trigger inside it
			// disabled, as on a department where editing the Schedule Template is blocked. The opener itself
			// is still worth showing; what follows it is not, because nothing can make those controls appear.
			openerCanReveal = !blockedOpeners.has(step.anchor);
		}
		if (openerVisible && (step.reveal === null || openerCanReveal)) kept.push(step);
	}
	return kept;
};

/**
 * How long joyride may poll for a step's control before giving up on it — the same budget for every step.
 *
 * Short because the wait cannot be used. While joyride waits, the overlay renders with NO spotlight cutout
 * and its full-screen path keeps `pointerEvents: auto`, so the screen is solid black and every click is
 * swallowed — the user cannot open the thing the tour is waiting for even if they now want to. Measured at
 * the old 3s: 3.03 seconds of a blank dark screen, then the step was skipped without a word. So the wait
 * can only cover a control that is already mounting (a MUI menu puts its content in the DOM on the click
 * and merely animates it in), and anything longer is a blackout. The tour holds the user on the
 * opener instead, which is the case this used to swallow.
 *
 * An EAGER step gets the same budget, not `TARGET_WAIT_MS`, because the cost of the wait is what sets it
 * and that cost does not depend on why the control is missing. `presentAnchors` already proved every eager
 * anchor visible before the tour launched and `survivingSteps` dropped the ones it did not, so joyride
 * failing to find one means it went away between the scan and the step, so polling 3s for something that
 * is already gone bought nothing but the blackout this constant exists to cap.
 *
 * ACQUISITION only. joyride applies this while hunting for a target it has not resolved yet; it
 * disconnects that finder the instant it succeeds and never re-checks, so a control that disappears AFTER
 * its step opened — the export popup closed out from under steps 3 and 4, or the export button dropping
 * its anchor when the date slider lands on a range with no exportable weeks — never reaches this path at
 * all. `watchAnchor` owns that half.
 */
export const MISSING_TARGET_WAIT_MS = 400;

/**
 * What `WalkthroughTooltip` needs that only the tour knows. joyride types `Step.data` as `any`; this is the
 * shape we actually put there.
 */
export interface WalkthroughStepData {
	/**
	 * The anchor NAME of the next step when that step is deferred, else null — i.e. what this step's
	 * control is supposed to reveal. The tooltip checks it before advancing so Next cannot walk into a step
	 * whose control the user never opened.
	 *
	 * A name rather than a selector so the check can go through `isAnchorVisible`. Asking only whether the
	 * node EXISTS called a mounted-but-hidden panel revealed, and Next then advanced into a step whose
	 * control nobody had opened — the same presence-for-visibility mistake `isAnchorVisible` documents.
	 */
	revealAnchor: string | null;
	/**
	 * This step's own anchor NAME, for the controller to watch while the step is showing.
	 *
	 * Carried on the step rather than re-derived from the walkthrough, because joyride reports a step by
	 * its index into the list it was GIVEN — already filtered by `survivingSteps` — so indexing the
	 * authored steps would drift the moment a department is missing one.
	 */
	anchor: string;
	/**
	 * Whether THIS step is the deferred one — i.e. its control only exists because an earlier step opened
	 * something. `revealAnchor` says what the NEXT step needs; this says what this step itself is.
	 *
	 * Three rules need it and none can derive it: the vanish watch must not read "the user used this
	 * control" as "the control was pulled away", Back must not walk into a surface that has since been
	 * closed, and neither can be answered from an anchor name.
	 */
	isDeferred: boolean;
	/**
	 * Whether the step BEFORE this one was deferred — i.e. whether going Back would return into a surface
	 * the tour has since closed. Computed here because only this function sees the whole list.
	 */
	afterDeferred: boolean;
	/** The PREVIOUS step's anchor name, or null on the first step — what Back would return to. */
	prevAnchor: string | null;
	/**
	 * The NEXT step's anchor name, or null on the last step. Used to decide whether advancing is even
	 * possible when this step's own control disappears — if the next control went with it, there is nowhere
	 * to advance TO and the tour should end instead.
	 */
	nextAnchor: string | null;
}

const isWalkthroughStepData = (data: unknown): data is WalkthroughStepData =>
	typeof data === "object" && data !== null && "anchor" in data && "revealAnchor" in data;

/** A joyride step's `data` (typed `any` by joyride) as ours, or null when it wasn't built by `joyrideSteps`. */
export const walkthroughStepData = (step: Step | undefined): WalkthroughStepData | null =>
	step !== undefined && isWalkthroughStepData(step.data) ? step.data : null;

/**
 * The surviving steps as joyride wants them. Pure, so the reveal wiring is testable without a render.
 *
 * `body` gets the step's resolved `bodyParams` alongside its key, so copy like "within the next
 * {{before_hours}}h" is interpolated with THIS department's value rather than rendering the placeholder.
 */
export const joyrideSteps = (steps: AnnieWalkthroughStep[], body: (bodyKey: string, bodyParams: Record<string, string> | null) => string): Step[] =>
	steps.map((step, index) => {
		const next = steps[index + 1];
		const data: WalkthroughStepData = {
			revealAnchor: next !== undefined && next.reveal !== null ? next.anchor : null,
			anchor: step.anchor,
			isDeferred: step.reveal !== null,
			afterDeferred: index > 0 && steps[index - 1]?.reveal !== null,
			prevAnchor: steps[index - 1]?.anchor ?? null,
			nextAnchor: next?.anchor ?? null,
		};
		return {
			target: anchorSelector(step.anchor),
			content: body(step.bodyKey, step.bodyParams),
			data,
			targetWaitTimeout: MISSING_TARGET_WAIT_MS,
			before: () => scrollAnchorIntoView(step.anchor),
		};
	});
